<template>
  <v-col :style="forPartyEdit ? 'padding: 0px;' : ''">
    <v-form
      ref="form"
      @submit.prevent="submitForm"
    >
      <div :class="forPartyEdit ? 'px-2' : 'pa-2'">
        <v-row
          v-if="topLabel"
          no-gutters
          class="mb-2"
        >
          <v-col
            class="pr-3"
            cols="auto"
          >
            <v-btn
              icon
              style="visibility: hidden;"
            >
              <v-icon>mdi-calendar</v-icon>
            </v-btn>
          </v-col>

          <v-col class="d-flex align-center">
            {{ topLabel }}
          </v-col>
        </v-row>

        <v-row align="start">
          <!-- SCHEME MENU ICON -->
          <v-col
            class="pr-3"
            cols="auto"
          >
            <v-menu
              open-on-hover
              offset-y
              transition="scale-transition"
            >
              <template #activator="slotProps">
                <v-btn
                  icon
                  v-bind="slotProps.attrs"
                  :disabled="readOnly"
                  v-on="slotProps.on"
                >
                  <v-icon>mdi-calendar</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="option of optionsDateFormat"
                  :key="option.id"
                  :class="{
                    'primary--text font-weight-bold': isSelected(option)
                  }"
                  @click="selectScheme(option)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ option.title }}
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-icon v-if="isSelected(option)">
                    <v-icon color="primary">
                      mdi-check
                    </v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <!-- DATE FIELD -->
          <v-col>
            <v-row>
              <v-menu
                ref="menu"
                v-model="menuOpen"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template #activator="{ on }">
                  <v-text-field
                    ref="dateField"
                    v-model="formatableDateFormatted"
                    :filled="!outlined"
                    :outlined="outlined"
                    :disabled="activeDateField"
                    :hint="hintText"
                    :label="calculatedLabel"
                    :placeholder="placeholderText"
                    persistent-hint
                    :rules="rules.date"
                    v-on="on"
                    @change="parseInputedDate"
                    @focus="focusOpen"
                    @keydown.tab="onTabOut"
                    @input="closeDatepickerMenu"
                  />
                </template>

                <v-date-picker
                  v-if="datePickable"
                  v-model="dateInput"
                  @input="closeDatepickerMenu"
                />
              </v-menu>

              <v-checkbox
                ref="checkboxField"
                v-model="boxChecked"
                class="ml-2 shrink"
                hide-details
                :disabled="disabled"
                :rules="rules.uncertain"
              />
            </v-row>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </v-col>
</template>

<script>
import { isValidDay, isValidMonth } from '@/utilities'
import dateFieldsMixin from '@/mixins/dateFields.js'
import moment from 'moment'

const FORMAT_MMDDYYYY = 'MM/DD/YYYY'
const FORMAT_MMYYYY = 'MM/YYYY'
const FORMAT_YYYY = 'YYYY'

export default {
  name: 'FormatSelectableComboDateField',
  mixins: [dateFieldsMixin],

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    errorMessages: {
      type: Array,
      default: _ => [],
    },

    forPartyEdit: {
      type: Boolean,
      default: false,
    },

    format: {
      type: String,
      default: '',
    },

    formatableDate: {
      type: String,
      default: '',
    },

    inactive: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    outlined: {
      type: Boolean,
      default: false,
    },

    topLabel: {
      type: String,
      default: null,
    },

    uncertain: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      formatableDateFormatted: '',
      menuOpen: false,

      optionsDateFormat: [
        { format: FORMAT_MMDDYYYY, id: 1, title: 'Month-Day-Year', value: null },
        { format: FORMAT_MMYYYY, id: 2, title: 'Month-Year', value: null },
        { format: FORMAT_YYYY, id: 3, title: 'Year', value: null },
        { format: FORMAT_MMDDYYYY, id: 4, title: 'Today', value: moment().format(FORMAT_MMDDYYYY) },
      ],

      rules: {
        date: [],
        uncertain: [],
      },

      selectedScheme: null,
    }
  },

  computed: {
    activeDateField () {
      if (!this.selectedScheme) return true
      if (this.readOnly) return true
      return this.selectedScheme.title === 'Today'
    },

    boxChecked: {
      get () {
        return this.uncertain === true
      },

      set (val) {
        this.$emit('update-box', val)
      },
    },

    calculatedLabel () {
      if (this.boxChecked && this.formatableDate) {
        return this.label + ' (estimated)'
      }
      return this.label
    },

    dateInput: {
      get () {
        return this.formatableDate
      },

      set (newDate) {
        this.formatableDateFormatted = moment(newDate).format(FORMAT_MMDDYYYY)
        this.parseInputedDate()
      },
    },

    datePickable () {
      return this.selectedScheme &&
             this.selectedScheme.title === 'Month-Day-Year'
    },

    hintText () {
      if (this.errorMessages.length > 0) {
        return this.errorMessages[0]
      }
      return 'Check box if an ' +
        (this.formatableDate ? 'Estimated' : 'Unknown') +
        ' date.'
    },

    placeholderText () {
      if (this.uncertainDate) return 'Unknown'
      if (!this.selectedScheme) return FORMAT_MMDDYYYY
      return this.selectedScheme.format
    },

    readOnly () {
      return this.uncertainDate || this.inactive || this.disabled
    },
  },

  watch: {
    formatableDate (val) {
      this.formatableDateFormatted = this.formatInputDate(val)
    },
  },

  created () {
    let format = this.format
    const allowed = [FORMAT_MMDDYYYY, FORMAT_MMYYYY, FORMAT_YYYY]

    if (allowed.indexOf(format) === -1) {
      format = FORMAT_MMDDYYYY
    }

    for (let i = 0; i < this.optionsDateFormat.length; i++) {
      if (this.optionsDateFormat[i].format === format) {
        this.selectedScheme = this.optionsDateFormat[i]
        break
      }
    }

    this.formatableDateFormatted = this.formatInputDate(this.dateInput)
  },

  methods: {
    closeDatepickerMenu () {
      this.menuOpen = false
    },

    formatInputDate (date) {
      if (!date || !this.selectedScheme) return date
      return moment(date).format(this.selectedScheme.format)
    },

    isSelected (option) {
      return this.selectedScheme &&
             this.selectedScheme.id === option.id
    },

    onTabOut () {
      this.closeDatepickerMenu()
      this.$emit('tab-out')
    },

    parseInputedDate () {
      let date = null
      const format = this.selectedScheme
        ? this.selectedScheme.format
        : null

      switch (format) {
        case FORMAT_YYYY:
          date = this.processYearFormatDate(this.formatableDateFormatted)
          break
        case FORMAT_MMYYYY:
          date = this.processMonthYearFormatDate(this.formatableDateFormatted)
          break
        case FORMAT_MMDDYYYY:
          date = this.processMonthDayYearFormatDate(this.formatableDateFormatted)
          break
      }

      if (format) {
        this.formatableDateFormatted = this.formatInputDate(date)
        this.updateDate(date)
      }
    },

    processMonthDayYearFormat (date, type, delimiter) {
      if (!date) return null

      let monthDayYear = null
      let month = null
      let day = null
      let year = null
      date = date.replace(' ', '')
      if (date.length !== type) return null

      if (delimiter) {
        monthDayYear = date.slice(0, type).split(delimiter)
        month = monthDayYear[0]
        day = monthDayYear[1]
        year = monthDayYear[2]
      } else {
        monthDayYear = date.slice(0, 8)
        month = monthDayYear.slice(0, 2)
        day = monthDayYear.slice(2, 4)
        year = monthDayYear.slice(4, 8)
      }

      if (/\d{4}$/.test(year.trim()) && isValidMonth(month) && isValidDay(day)) {
        return `${year}-${month}-${day}`
      } else {
        return null
      }
    },

    processMonthDayYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.includes('-')) {
        return this.processMonthDayYearFormat(date, 10, '-')
      } else if (date.includes('/')) {
        return this.processMonthDayYearFormat(date, 10, '/')
      } else if (date.length === 8) {
        return this.processMonthDayYearFormat(date, 8)
      } else {
        return null
      }
    },

    processMonthYearFormat (date, type, delimiter) {
      if (!date) return null

      let monthYear = null
      let month = null
      let year = null
      date = date.replace(' ', '')
      if (date.length !== type) return null

      if (delimiter) {
        monthYear = date.slice(0, type).split(delimiter)
        month = monthYear[0]
        year = monthYear[1]
      } else {
        monthYear = date.slice(0, 6)
        month = monthYear.slice(0, 2)
        year = monthYear.slice(2, 6)
      }

      if (/\d{4}$/.test(year.trim()) && isValidMonth(month)) {
        return `${year}-${month}-01`
      } else {
        return null
      }
    },

    processMonthYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.includes('-')) {
        return this.processMonthYearFormat(date, 7, '-')
      } else if (date.includes('/')) {
        return this.processMonthYearFormat(date, 7, '/')
      } else if (date.length === 6) {
        return this.processMonthYearFormat(date, 6)
      } else {
        return null
      }
    },

    processYearFormat (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (!/\d{4}$/.test(date.trim())) return null

      return `${date}-01-01`
    },

    processYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.length === 4) {
        return this.processYearFormat(date, 4)
      } else {
        return null
      }
    },

    selectScheme (option) {
      this.selectedScheme = option
      this.formatableDateFormatted = option.value
      this.parseInputedDate()
    },

    submitForm () {
      this.$emit('submit')
    },

    updateDate (date) {
      this.$emit('update-date', {
        date: date,
        format: this.selectedScheme
          ? this.selectedScheme.format
          : null,
      })
    },
  },
}
</script>
